import { FolderOpen } from 'lucide-react';
import { Button, Select } from '../../components/Primitives.jsx';
import { buildSourceEntries } from '../../data.js';

// "Source" selector — every Source-anchored block (Group By, Unpivot,
// Compute, Case When, Lookup, Union, …) picks which dataset it reads from
// here. One list over the pipeline's single source namespace
// (buildSourceEntries in data.js, mirroring server/app/engine/registry.py):
//   • "self" — the block's own current input rows. Stored as the
//     "__self__" sentinel and never looked up by name.
//   • sheets kept at upload time, uploaded files from Browse, and EARLIER
//     steps' Output Names — a later step's output doesn't exist yet when
//     this one runs. Options render through a native <select> (Select in
//     Primitives.jsx), so the "where this name came from" hint is a plain
//     text suffix, not an icon — <option> can't hold a React node.
//     Resolution doesn't care where a name came from; names are unique
//     across all three.
// Backend counterpart: _resolve_excel_source() in
// server/app/engine/steps/_source_resolution.py.
//
// `buildTargetName` (pipeline.inputFile?.buildTargetName) only changes the
// "self" option's label; older pipelines without it keep the generic label.
export function SourceSelect({ value, onChange, secondaryDatasets, sheets, pipeline, currentStep, onBrowse, buildTargetName, placeholder }) {
  const selfLabel = buildTargetName
    ? `${buildTargetName} (self — live pipeline state)`
    : "This block's own output (self)";
  const selfOrder = currentStep?.order ?? 0;
  const options = [{ value: '__self__', label: selfLabel }];
  // Optional "nothing picked yet" entry for callers where a blank source is
  // different from self (exists-in-source conditions).
  if (placeholder) options.unshift({ value: '', label: placeholder });
  for (const entry of buildSourceEntries({ sheets, secondaryDatasets, steps: pipeline?.steps })) {
    if (entry.kind === 'step') {
      const s = entry.step;
      if (!currentStep || s.id === currentStep.id || (s.order ?? 0) >= selfOrder) continue;
      options.push({ value: entry.name, label: `${entry.name} (from "${s.label || s.type}")` });
    } else {
      options.push({ value: entry.name, label: `${entry.name} (${entry.kind === 'sheet' ? 'sheet' : 'file'})` });
    }
  }
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <Select value={placeholder ? (value || '') : (value || '__self__')} onChange={onChange} options={options} style={{ flex: 1 }} />
      {onBrowse && <Button kind="ghost" size="sm" icon={<FolderOpen size={14} />} onClick={onBrowse}>Add file…</Button>}
    </div>
  );
}
